import React from "react";
import PnoeDetailSection, { PnoeField } from "./PnoeDetailSection";
import { useAuth } from "../../../../providers/AuthProvider";
import {
  convertWeightToDisplay,
  formatHeightDisplay,
  getWeightUnit,
} from "../logicBuilder/measurementUnitHelpers";
import { PnoeParticipant, toNumber } from "../../entities/pnoeMeasurement";
import { PnoeParameterOptions } from "./usePnoeParameters";
import { titleCaseOption } from "./pnoeFieldFormatting";
import { PnoeLifestyleRow } from "./pnoeLifestyle";
import {
  cmToInchesPrecise,
  inchesToFtAndInPrecise,
  kgToLbsPrecise,
} from "./pnoeUnitDisplay";

/**
 * Every displayable participant field, in reading order: the grid flows three
 * per row, and the lifestyle questions follow straight on. The legacy
 * `exercise_frequency` is deliberately not shown: the lifestyle set replaced
 * it, the service stores null for it on new tests, and the review asked for
 * it to go from the page and the panel alike — a historical value on an old
 * test is not worth showing either.
 *
 * "option" fields resolve their display label through the breath service's
 * `configuration` endpoint.
 *
 * `customer_name` is deliberately absent: the design's "Client" section shows
 * no name, because whose test this is is already answered above — by the
 * header's client badge on the cross-client page, and by the shell's top bar
 * in both workspaces.
 *
 * The API always stores kg/cm; weight and height render in the affiliate's
 * preferred unit (Settings → Measurement unit), so they carry their own kinds.
 */
export const PARTICIPANT_FIELDS = [
  { key: "weight_kg", label: "Weight", kind: "weight" as const },
  { key: "height_cm", label: "Height", kind: "height" as const },
  { key: "race", label: "Race", kind: "option" as const },
  { key: "fvc_lt", label: "FVC", kind: "decimal" as const, unit: "L" },
  { key: "gender", label: "Gender", kind: "option" as const },
  { key: "age", label: "Age", kind: "plain" as const, unit: "years" },
] as const;

export type ParticipantField = (typeof PARTICIPANT_FIELDS)[number];

interface PnoeParticipantSectionProps {
  participant: PnoeParticipant;
  options: PnoeParameterOptions;
  /**
   * The lifestyle questionnaire answered at test time, from
   * `buildLifestyleRows`. Rendered after the fixed fields in the same grid;
   * empty on a historical measurement with no snapshot.
   */
  lifestyleRows?: PnoeLifestyleRow[];
  /** Right-hand slot in the section header — the status badges live here. */
  headerRight?: React.ReactNode;
  /** Opens the edit panel. Omitted hides the pencil. */
  onEdit?: () => void;
  /** Greys the pencil out, with this as its tooltip. */
  editDisabled?: boolean;
  editDisabledReason?: string;
}

/**
 * The "Client" section of the test detail: the participant's details and the
 * lifestyle questions in one three-per-row grid, behind one pencil — both are
 * edited in the same panel and sent by the same PATCH.
 *
 * Shown in full on the page — the old measurement page hid half of these behind
 * a modal opened from the "Subject" title, which the requirements call out.
 * Editing happens in the side panel behind the pencil, not inline.
 *
 * A lifestyle value is the label the client was shown and chose (the
 * snapshot), not re-resolved against the current wording; its help text sits
 * behind an info icon beside the question.
 */
const PnoeParticipantSection: React.FC<PnoeParticipantSectionProps> = ({
  participant,
  options,
  lifestyleRows = [],
  headerRight,
  onEdit,
  editDisabled,
  editDisabledReason,
}) => {
  // 1 = metric, 2 = imperial. Set per affiliate in Settings.
  const { AuthUser } = useAuth();
  const unitType = AuthUser?.unitType;

  const renderValue = (field: ParticipantField) => {
    const raw = (participant as any)[field.key];
    if (raw === null || raw === undefined || raw === "") return "";
    const unit = "unit" in field ? ` ${field.unit}` : "";

    switch (field.kind) {
      case "option": {
        const match = options[field.key]?.find(
          (option) => option.value === String(raw)
        );
        return match?.label ?? titleCaseOption(String(raw));
      }
      case "weight": {
        const kg = toNumber(raw);
        if (kg === null) return "";
        // One decimal in both systems; the shared converter rounds lbs to a
        // whole number, so the PNOĒ detail uses its own precise variant.
        const shown =
          unitType === 2
            ? kgToLbsPrecise(kg)
            : Number(convertWeightToDisplay(kg, unitType).toFixed(1));
        return `${shown} ${getWeightUnit(unitType)}`;
      }
      case "height": {
        const cm = toNumber(raw);
        if (cm === null) return "";
        // Imperial keeps a decimal on the inches ("5 ft 9.5 in"); the shared
        // formatter rounds to whole inches. Metric stays whole cm.
        if (unitType === 2) {
          const { feet, inches } = inchesToFtAndInPrecise(cmToInchesPrecise(cm));
          return `${feet} ft ${inches} in`;
        }
        return formatHeightDisplay(Math.round(cm), unitType);
      }
      case "decimal": {
        const parsed = toNumber(raw);
        return parsed === null ? "" : `${parsed}${unit}`;
      }
      case "plain":
        return `${raw}${unit}`;
      default:
        return String(raw);
    }
  };

  return (
    <PnoeDetailSection
      title="Client"
      variant="inline"
      headerRight={headerRight}
      onEdit={onEdit}
      editDisabled={editDisabled}
      editDisabledReason={editDisabledReason}
    >
      {PARTICIPANT_FIELDS.map((field) => (
        <PnoeField
          key={field.key}
          label={field.label}
          value={renderValue(field)}
        />
      ))}
      {lifestyleRows.map((row) => (
        <PnoeField
          key={row.key}
          label={row.question}
          value={row.label}
          hint={row.details || undefined}
        />
      ))}
    </PnoeDetailSection>
  );
};

export default PnoeParticipantSection;
